<template>
  <div class="header-wrapper" style="background-color: transparent !important;">
    <!-- 顶部固定层 -->
    <div
      class="top-overlay"
      :class="{
        'show-border': borderVisible,
        'search-active': showSearch,
        'is-fixed': fixed,
      }"
      :style="{
        backgroundColor: showSearch ? '#ffffff' : backgroundColor,
        color:
          theme === 'dark' && !showSearch ? '#ffffff' : '#000000',
      }"
      v-if="!menuOpen"
    >
      <!-- 左侧：Menu + Login/Account -->
      <div class="top-left-nav" :class="{ 'nav-hide': hideSideNav }">
        <button class="menu-btn font-apercu-regular" @click.stop="openMenu">Menu</button>
        <a
          href="#"
          class="login-link font-apercu-regular"
          @click.prevent="isLoggedIn ? openUserInfo() : openLogin()"
        >
          {{ isLoggedIn ? 'Account' : 'Login' }}
        </a>
      </div>

      <!-- 统一 Logo -->
      <div
        class="logo-unified"
        :class="{ 'is-large': showLargeLogo }"
        v-if="showLogo"
      >
        <img :src="logoUrl" alt="denHolm" @click="goToHome" />
      </div>

      <!-- 右侧：三个图标 -->
      <div class="top-right-icons" :class="{ 'icons-hide': hideSideNav }">
        <button class="icon-btn" @click.stop="openMessagePopup">
          <img
            :src="
              effectiveIconColor === 'white'
                ? require('@/assets/icon/logo.png')
                : require('@/assets/icon/logo_black.png')
            "
            alt="user"
            class="icon-img"
          />
        </button>
        <button class="icon-btn" @click.stop="openEnquiry">
          <img :src="enquiryIcon" alt="product" class="icon-img" />
        </button>
        <button class="icon-btn" @click.stop="toggleSearch">
          <img
            :src="
              effectiveIconColor === 'white'
                ? require('@/assets/icon/search.png')
                : require('@/assets/icon/search_black.png')
            "
            alt="search"
            class="icon-img"
            style="width: 15rem;"
          />
        </button>
      </div>
    </div>

    <!-- 左侧菜单栏 -->
    <aside class="sidebar-wrap" :style="{ pointerEvents: menuOpen ? 'auto' : 'none' }">
      <Menu
        :isOpen="menuOpen"
        @close="closeMenu"
        @toggle-search="toggleSearch"
        @toggle-user-info="openUserInfo"
        @toggle-enquiry="openEnquiry"
        @toggle-login="openLogin"
        @toggle-subscribe="openEmailPopup"
      />
    </aside>

    <!-- 搜索框（保持在顶部栏下方，整块白色从上方滑入） -->
    <transition name="search-slide">
      <div v-if="showSearch" class="search-overlay" @scroll="handleScroll">
        <div class="search-container">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="What are you looking for?"
            class="search-input font-apercu-medium"
            @keyup.enter="performSearch"
            autofocus
            @blur="handleBlur"
            @input="handleInput"
          />
          <div
            v-if="showSearchResult && !searchConfirmed"
            class="search-result-hint font-apercu-medium"
          >
            Press enter to view all {{ searchTotal }} results
          </div>
        </div>
        <div v-if="showSearchResult" class="search-results-grid">
          <div
            class="result-item"
            v-for="(item, index) in searchResults"
            :key="index"
          >
            <div class="result-image-wrapper">
              <img
                :src="item.image"
                :alt="item.name"
                @click="goToProduct(item.ProductID)"
              />
            </div>
          </div>
        </div>
        <div v-if="isLoadingMore" class="loading-indicator">Loading...</div>
      </div>
    </transition>

    <!-- 搜索遮罩层 -->
    <div v-if="showSearch" class="search-mask" @click="toggleSearch"></div>

    <!-- 登录组件 -->
    <Login
      :is-open="isLoginOpen"
      :initial-customer-type="loginInitialType"
      :initial-isregister="initialIsRegister"
      @open-email-popup="openEmailPopup"
	  @open-user-panel="openUserInfo" 
      @close="closeLogin"
      @switch-to-menu="switchToMenu"
    />

    <!-- 用户信息组件 -->
    <UserInfo
      :is-open="isUserInfoOpen"
      @close="closeUserInfo"
      @open-menu="openMenu"
      @toggle-search="toggleSearch"
      @toggle-enquiry="openEnquiry"
      @open-trader-login="openTraderLogin"
      @open-private-login="openPrivateLogin"
      @open-register="openRegister"
    />

    <!-- Enquiry Component -->
    <Enquiry
      :is-open="isEnquiryOpen"
      @close="closeEnquiry"
      @toggle-search="toggleSearch"
      @toggle-user-info="openUserInfo"
      @open-email-popup="openEmailPopup"
    />

    <!-- 邮件弹窗组件 -->
    <EmailPopup
      :visible.sync="emailPopupVisible"
      :auto-show-delay="0"
      @close="onEmailPopupClose"
    />
  </div>
</template>

<script>
import logoBlack from '../assets/logo_b.png'          // 大图
import logoBlackSmall from '../assets/logo_b_mini.png' // 小图
import logoWhite from '../assets/logo_w.png'
import Login from './Login.vue'
import UserInfo from './UserInfo.vue'
import Enquiry from './Enquiry.vue'
import Menu from './Menu.vue'
import EmailPopup from './Email.vue'

import { getUser } from '@/config/auth'
import { searchProducts } from '@/api/product'
import { getCartItems } from '@/api/Enquiry'

// 购物车图标导入（保持不变）
import productIcon from '@/assets/icon/product.png'
import productBlackIcon from '@/assets/icon/product_black.png'
import icon1White from '@/assets/cart/1-white.png'
import icon1Black from '@/assets/cart/1-black.png'
import icon2White from '@/assets/cart/2-white.png'
import icon2Black from '@/assets/cart/2-black.png'
import icon3White from '@/assets/cart/3-white.png'
import icon3Black from '@/assets/cart/3-black.png'
import icon4White from '@/assets/cart/4-white.png'
import icon4Black from '@/assets/cart/4-black.png'
import icon5White from '@/assets/cart/5-white.png'
import icon5Black from '@/assets/cart/5-black.png'
import icon6White from '@/assets/cart/6-white.png'
import icon6Black from '@/assets/cart/6-black.png'
import icon7White from '@/assets/cart/7-white.png'
import icon7Black from '@/assets/cart/7-black.png'
import icon8White from '@/assets/cart/8-white.png'
import icon8Black from '@/assets/cart/8-black.png'
import icon9White from '@/assets/cart/9-white.png'
import icon9Black from '@/assets/cart/9-black.png'
import icon9WhitePlus from '@/assets/cart/9-white+.png'
import icon9BlackPlus from '@/assets/cart/9-black+.png'

export default {
  name: 'Header',
  components: {
    Login,
    UserInfo,
    Enquiry,
    Menu,
    EmailPopup,
  },
  props: {
    theme: { type: String, default: 'dark' },
    showBorder: { type: Boolean, default: false },
    iconColor: { type: String, default: 'white' },
    showLogo: { type: Boolean, default: true },
    fixed: { type: Boolean, default: false },
    backgroundColor: { type: String, default: 'transparent' },
    smallLogoWidth: { type: Number, default: 188 },
    smallLogoHeight: { type: Number, default: 21 },
    borderVisible: { type: Boolean, default: false },
    hideSideNav: { type: Boolean, default: false },
    logoShrink: { type: Boolean, default: false },
    smallLogoWidthShrink: { type: Number, default: 188 },
    smallLogoHeightShrink: { type: Number, default: 21 },
	isSticky: {
	  type: Boolean,
	  default: false
	}
  },
  data() {
    return {
      autoPopupTimer: null,
      autoPopupShown: false,
      menuOpen: false,
      showSearch: false,
      searchQuery: '',
      showSearchResult: false,
      searchConfirmed: false,
      isLoadingMore: false,
      isLoginOpen: false,
      isUserInfoOpen: false,
      isEnquiryOpen: false,
      emailPopupVisible: false,
      loginInitialType: 'private',
      initialIsRegister: false,
       logoBlack,
            logoBlackSmall,
            logoWhite,
      isAnimating: false,
      searchLoading: false,
      searchPageIndex: 1,
      searchTotal: 0,
      searchHasMore: true,
      searchResults: [],
      currentUser: null,
      cartCount: 0,
      iconMap: {
        'product-white': productIcon,
        'product-black': productBlackIcon,
        '1-white': icon1White,
        '1-black': icon1Black,
        '2-white': icon2White,
        '2-black': icon2Black,
        '3-white': icon3White,
        '3-black': icon3Black,
        '4-white': icon4White,
        '4-black': icon4Black,
        '5-white': icon5White,
        '5-black': icon5Black,
        '6-white': icon6White,
        '6-black': icon6Black,
        '7-white': icon7White,
        '7-black': icon7Black,
        '8-white': icon8White,
        '8-black': icon8Black,
        '9-white': icon9White,
        '9-black': icon9Black,
        '9+-white': icon9WhitePlus,
        '9+-black': icon9BlackPlus,
      }
    }
  },
  mounted() {
    this.scheduleAutoPopup()
    this.currentUser = getUser()
    this.fetchCartCount()
    this.$root.$on('login-success', this.updateUser)
    this.$root.$on('logout-success', this.updateUser)
    this.$root.$on('update-cart-count', this.updateCartCount)
	this.$root.$on('open-user-panel', this.openUserInfo)
	this.$root.$on('open-enquiry', this.openEnquiry)
  },
  beforeDestroy() {
    if (this.autoPopupTimer) clearTimeout(this.autoPopupTimer)
    this.$root.$off('login-success', this.updateUser)
    this.$root.$off('logout-success', this.updateUser)
    this.$root.$off('update-cart-count', this.updateCartCount)
	this.$root.$off('open-user-panel', this.openUserInfo)
	this.$root.$off('open-enquiry', this.openEnquiry)
  },
  computed: {
    // ★ 修复1：大 Logo 仅在未收缩时显示
    showLargeLogo() {
      return !this.showBorder && !this.showSearch && !this.hideSideNav && !this.logoShrink && !this.isSticky
    },
    // ★ 修复2：Logo 颜色跟随 effectiveIconColor（搜索时变黑）
   logoUrl() {
         if (this.effectiveIconColor === 'white') {
           return this.logoWhite
         } else {
           return this.logoShrink ? this.logoBlackSmall : this.logoBlack
         }
       },
    isLoggedIn() {
      return !!this.currentUser && !!this.currentUser.UserID
    },
    isLoggedInAndSubscribed() {
      return this.currentUser && this.currentUser.IsSubscribed === true
    },
    autoShowDelay() {
      return this.$route.name === 'Home' ? 5000 : 0
    },
    effectiveIconColor() {
      return this.showSearch ? 'black' : this.iconColor
    },
    currentOpenComponent() {
      if (this.isLoginOpen) return 'login'
      if (this.isUserInfoOpen) return 'userInfo'
      if (this.isEnquiryOpen) return 'enquiry'
      if (this.showSearch) return 'search'
      if (this.menuOpen) return 'menu'
      return null
    },
    enquiryIcon() {
      const count = this.cartCount
      const color = this.effectiveIconColor
      let key
      if (count === 0) {
        key = 'product'
      } else if (count <= 9) {
        key = String(count)
      } else {
        key = '9+'
      }
      const fullKey = `${key}-${color}`
      return this.iconMap[fullKey] || this.iconMap['product-white']
    }
  },
  methods: {
    // 全部方法保持原样，无改动（此处为节省篇幅省略，实际使用时应保留完整）
    // 请参考你提供的原始代码中的 methods 部分
    async fetchCartCount() {
      const user = getUser()
      if (user && user.UserID) {
        try {
          const items = await getCartItems(user.UserID)
          this.cartCount = items.length || 0
        } catch (error) {
          console.error('Failed to fetch cart count:', error)
          this.cartCount = 0
        }
      } else {
        const raw = localStorage.getItem('pending_enquiry_items')
        const list = raw ? JSON.parse(raw) : []
        this.cartCount = list.length
      }
    },
    updateUser() {
      this.currentUser = getUser()
      this.fetchCartCount()
    },
    updateCartCount(count) {
      this.cartCount = count || 0
    },
    scheduleAutoPopup() {
      if (this.autoPopupTimer) clearTimeout(this.autoPopupTimer)
      const delay = this.autoShowDelay
      if (delay <= 0) return
      const hasAutoShown = localStorage.getItem('emailAutoShown') === 'true'
      if (hasAutoShown) return
      if (this.autoPopupShown) return
      if (this.isLoggedInAndSubscribed) return

      this.autoPopupTimer = setTimeout(() => {
        if (this.isLoggedInAndSubscribed) return
        if (localStorage.getItem('emailAutoShown') === 'true') return
        if (!this.emailPopupVisible && !this.autoPopupShown) {
          this.emailPopupVisible = true
          this.autoPopupShown = true
        }
        this.autoPopupTimer = null
      }, delay)
    },
    onEmailPopupClose() {
      localStorage.setItem('emailAutoShown', 'true')
      this.emailPopupVisible = false
    },
    goToHome() {
      this.$router.push({ name: 'Home' })
    },
    goToProduct(productId) {
      this.$router.push({
        path: '/product/detail',
        query: { productId },
      })
    },
    openEmailPopup() {
      this.closeLogin()
      this.emailPopupVisible = true
    },
    closeAllComponents() {
      const component = this.currentOpenComponent
      this.isLoginOpen = false
      this.isUserInfoOpen = false
      this.isEnquiryOpen = false
      this.showSearch = false
      this.searchQuery = ''
      this.showSearchResult = false
      this.menuOpen = false

      if (!component) return Promise.resolve()
      let duration = 400
      if (component === 'enquiry' || component === 'menu' || component === 'login')
        duration = 800
      return new Promise((resolve) => setTimeout(resolve, duration))
    },
    async openMenu() {
      if (this.isAnimating) return
      const hadOpen = !!this.currentOpenComponent
      if (hadOpen) {
        this.isAnimating = true
        await this.closeAllComponents()
      }
      this.menuOpen = true
      document.body.style.overflow = 'hidden'
      this.$emit('menu-open')
      if (hadOpen) this.isAnimating = false
    },
    closeMenu() {
      this.menuOpen = false
      document.body.style.overflow = ''
      this.$emit('menu-close')
    },
    async openLogin() {
      if (this.isAnimating) return
      const hadOpen = !!this.currentOpenComponent
      if (hadOpen) {
        this.isAnimating = true
        await this.closeAllComponents()
      }
      this.isLoginOpen = true
      document.body.style.overflow = 'hidden'
      if (hadOpen) this.isAnimating = false
    },
    closeLogin() {
      this.isLoginOpen = false
      document.body.style.overflow = ''
    },
    async openUserInfo() {
	  this.isLoginOpen = false
      if (this.isAnimating) return
      const hadOpen = !!this.currentOpenComponent
      if (hadOpen) {
        this.isAnimating = true
        await this.closeAllComponents()
      }
      this.isUserInfoOpen = true
      document.body.style.overflow = 'hidden'
      if (hadOpen) this.isAnimating = false
    },
    closeUserInfo() {
      this.isUserInfoOpen = false
      document.body.style.overflow = ''
    },
    async openEnquiry() {
      if (this.isAnimating) return
      const hadOpen = !!this.currentOpenComponent
      if (hadOpen) {
        this.isAnimating = true
        await this.closeAllComponents()
      }
      this.isEnquiryOpen = true
      document.body.style.overflow = 'hidden'
      if (hadOpen) this.isAnimating = false
    },
    openMessagePopup() {
      this.openEmailPopup()
    },
    closeEnquiry() {
      this.isEnquiryOpen = false
      document.body.style.overflow = ''
    },
    switchToMenu() {
      this.openMenu()
    },
    openTraderLogin() {
      this.loginInitialType = 'trader'
      this.openLogin()
    },
    openPrivateLogin() {
      this.loginInitialType = 'private'
      this.openLogin()
    },
    openRegister() {
      this.initialIsRegister = true
      this.loginInitialType = 'private'
      this.openLogin()
    },
    async toggleSearch() {
		
      if (this.isAnimating) return
      if (this.showSearch) {
        // this.showBorder = false
        this.showSearch = false
        this.showSearchResult = false
        this.searchConfirmed = false
        document.body.style.overflow = ''
        this.menuOpen = false
      } else {
        const hadOpen = !!this.currentOpenComponent
        if (hadOpen) {
          this.isAnimating = true
          await this.closeAllComponents()
        }
        // this.showBorder = false
        this.showSearch = true
        this.showSearchResult = false
        this.searchConfirmed = false
        document.body.style.overflow = 'hidden'
        if (hadOpen) this.isAnimating = false
      }
    },
    handleInput() {
      this.showSearchResult = false
      this.searchConfirmed = false
    },
    async performSearch() {
      if (!this.searchQuery.trim()) return
      this.searchResults = []
      this.searchPageIndex = 1
      this.searchHasMore = true
      this.showSearchResult = true
      this.searchConfirmed = true
      await this.fetchSearchResults(this.searchQuery, 1)
    },
    async fetchSearchResults(keyword, pageIndex) {
      this.searchLoading = true
      try {
        const response = await searchProducts({
          keyword,
          pageIndex,
          pageSize: 20000,
        })
        if (response.retCode === 1) {
          const products = response.result || []
          const total = response.totalCount || 0
          this.searchTotal = total
          this.searchConfirmed = false
          const formatted = products.map((p) => ({
            ProductID: p.ProductID,
            name: p.ProductName,
            image:
              p.Images.find((img) => img.IsPrimary).ImageURL ||
              p.Images[0].ImageURL ||
              '',
          }))
          if (pageIndex === 1) {
            this.searchResults = formatted
          } else {
            this.searchResults = [...this.searchResults, ...formatted]
          }
          this.searchHasMore = this.searchResults.length < total
          this.searchPageIndex = pageIndex
        }
      } catch (error) {
        console.error('Search error:', error)
      } finally {
        this.searchLoading = false
      }
    },
    handleBlur() {
      this.showSearchResult = this.searchQuery !== ''
    },
    handleScroll(e) {
      if (!this.showSearchResult || this.isLoadingMore) return
      const { scrollTop, clientHeight, scrollHeight } = e.target
      if (scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMoreResults()
      }
    },
    async loadMoreResults() {
      if (!this.searchHasMore || this.searchLoading || !this.searchQuery) return
      const nextPage = this.searchPageIndex + 1
      await this.fetchSearchResults(this.searchQuery, nextPage)
    },
  },
}
</script>

<style scoped>
/* 所有样式与原始代码保持一致，仅修改了 .search-overlay 的 top 和 padding */
.header-wrapper {
  position: relative;
  min-height: 74rem;
  background: transparent;
}

.top-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 23rem 40rem 28rem;
  z-index: 100;
  height: 74rem;
  box-sizing: border-box;
  transition: border-bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              background-color 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              color 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.top-overlay.is-fixed {
  position: fixed;
  width: 100%;
}

.top-overlay.search-active {
  background-color: #ffffff;
  border: none;
  position: fixed;
}

.top-overlay.search-active .menu-btn,
.top-overlay.search-active .login-link,
.top-overlay.search-active .icon-btn {
  color: #000000;
}

.top-overlay.show-border {
  border-bottom: 1rem solid #000;
}

.top-overlay > * {
  pointer-events: auto;
}

.top-left-nav {
  display: flex;
  gap: 18rem;
  align-items: center;
  flex-shrink: 0;
  z-index: 1;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease;
}
.top-left-nav.nav-hide {
  transform: translateY(-30rem);
  opacity: 0;
  pointer-events: none;
}

.top-right-icons {
  display: flex;
  gap: 18rem;
  align-items: center;
  flex-shrink: 0;
  z-index: 1;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease;
}
.top-right-icons.icons-hide {
  transform: translateY(-30rem);
  opacity: 0;
  pointer-events: none;
}

.logo-unified {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(1);
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  transition: top 0.6s cubic-bezier(0.34, 1.56, 0.64, 1),
              transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1),
              width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.logo-unified img {
  width: 188rem;
  height: auto;
  display: block;
  cursor: pointer;
  pointer-events: auto;
  transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1),
              height 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.logo-unified.is-large {
  top: 74rem;
  transform: translateX(-50%) scale(1);
  width: calc(100% - 62rem);
}

.logo-unified.is-large img {
  width: 100%;
  height: auto;
  max-width: 100%;
}

.logo-unified:not(.is-large) {
  top: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  width: auto;
}

.logo-unified:not(.is-large) img {
  width: 188rem;
  height: auto;
}

.menu-btn {
  background: none;
  border: none;
  color: inherit;
  font-size: 16rem;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.3s;
}
.menu-btn:hover {
  opacity: 0.7;
}

.login-link {
  color: inherit;
  text-decoration: none;
  font-size: 16rem;
  font-weight: 400;
  transition: opacity 0.3s;
}
.login-link:hover {
  opacity: 0.7;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s;
}
.icon-btn:hover {
  opacity: 0.7;
}
.icon-img {
  width: auto;
  height: 17rem;
}

/* 搜索遮罩 */
.search-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 90;
}

/* ★ 搜索面板：保持在顶部栏下方，纯白，从上方滑入 */
.search-overlay {
  position: fixed;
  top: 74rem;               /* 与顶部栏高度一致 */
  left: 0;
  right: 0;
  z-index: 99;
  background: #ffffff;      /* 纯白，无渐变 */
  padding: 23rem 40rem 28rem; /* 与顶部栏内边距对齐 */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-bottom: 1rem solid #000;
  max-height: calc(100vh - 74rem);
  overflow-y: auto;
  /* 动画由 transition 控制，此处不设 */
}

.search-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 600rem;
}

.search-input {
  font-size: 24rem;
  font-weight: 500;
  width: 100%;
  font-family: inherit;
  height: 50rem;
  line-height: 50rem;
  outline: none;
  border: none;
  border-bottom: 1rem solid #000;
  background: transparent;
  text-align: left;
  padding: 0;
}

.search-result-hint {
  width: 100%;
  text-align: left;
  margin-top: 10rem;
  font-size: 16rem;
  color: #000;
  display: flex;
  align-items: center;
}

.search-results-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  margin-top: 40rem;
}

.result-item {
  width: 100%;
}
.result-image-wrapper {
  width: 100%;
  height: 378rem;
  aspect-ratio: 1;
  background-color: #f5f5f5;
  overflow: hidden;
}
.result-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.loading-indicator {
  width: 100%;
  text-align: center;
  padding: 20rem;
  font-size: 16rem;
  color: #666;
}

/* 动画：从上方滑入（相对自身高度） */
.search-slide-enter-active,
.search-slide-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
}
.search-slide-enter,
.search-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
.search-slide-enter-to,
.search-slide-leave {
  transform: translateY(0);
  opacity: 1;
}

.sidebar-wrap {
  position: fixed;
  top: 0;
  left: 0;
  width: 35%;
  height: 100vh;
  z-index: 1000;
  overflow: hidden;
}

@media (max-width: 1024px) {
  .sidebar-wrap {
    width: 40%;
  }
}
@media (max-width: 768px) {
  .top-overlay {
    padding: 20rem;
  }
  .top-overlay.search-active {
    height: 100rem;
  }
  .search-overlay {
    top: 100rem;
    padding: 20rem;
    max-height: calc(100vh - 100rem);
  }
  .top-left-nav {
    gap: 15rem;
  }
  .top-right-icons {
    gap: 10rem;
  }
  .search-input {
    width: 180rem;
  }
  .logo-unified.is-large {
    width: calc(100% - 40rem);
  }
  .logo-unified:not(.is-large) img {
    width: 188rem;
    height: auto;
  }
  .sidebar-wrap {
    width: 50%;
  }
}
@media (max-width: 480px) {
  .sidebar-wrap {
    width: 100%;
  }
}
</style>